import { IonCol, IonContent, IonRow, IonGrid, IonCard, IonCardContent, IonCardHeader, IonButton, useIonViewDidEnter, IonIcon } from '@ionic/react';
import Header from '../components/Header/Header';
import { onScroll } from '../services/sticky-menu.service';
import Footer from '../components/Footer';
import CheckoutBody from '../components/Shop/CheckoutBody';
import CartSubTotal from '../components/Shop/CartSubTotal';
import { useAppContext } from '../services/app-context.service';
import { authModalRef } from '../components/Auth/AuthModal';
import { documentBaseTitle, appSubDir } from '../configs/app';
import ReactGA from "react-ga4";
import { logInOutline, cartOutline, alertCircleOutline } from 'ionicons/icons';
import ProgressiveImg from '../components/ProgressiveImg';
import { getProductImageUrl } from '../functions/product.functions';
import { preventOverflow } from '../functions/string.functions';
import './Checkout.scss';

const BDT = new Intl.NumberFormat('en-IN');

const Checkout: React.FC = () => {
    const { user, cart, settings } = useAppContext();
    const minOrderAmount = settings?.min_order_amount ?? 1000;
    const minOrderItemCount = settings?.min_order_item_count ?? 3;

    useIonViewDidEnter(() => {
        const tabBar = document.querySelector('ion-tab-bar');
        if (tabBar !== null) {
            tabBar.style.display = 'none';
        }
        const title = "Checkout";
        ReactGA.send({ hitType: "pageview", page: window.location.pathname, title });
        document.getElementsByTagName("title")[0].innerHTML = title + " | " + documentBaseTitle;
    });

    const isCartEmpty = cart.itemCount < 1;
    const isMinAmountNotMet = cart.total < minOrderAmount;
    const isMinItemsNotMet = cart.itemCount < minOrderItemCount;
    const areConditionsMet = !isMinAmountNotMet && !isMinItemsNotMet;

    return (
        <IonContent fullscreen scrollEvents={true} onIonScroll={onScroll}>
            <Header />
            <IonRow>
                <IonCol sizeXl='10' offsetXl='1' sizeLg='10' offsetLg='1' className='no-padding bg-content' id="checkout-page-container">
                    <div className="checkout-banner">
                        <div className='title'>
                            <h1>Checkout</h1>
                            <hr />
                            <p>Complete your order information securely</p>
                        </div>
                    </div>

                    <IonGrid className="checkout-grid">
                        {isCartEmpty ? (
                            <IonRow className="ion-justify-content-center">
                                <IonCol sizeXs="12" sizeMd="6">
                                    <IonCard className="empty-cart-card ion-text-center">
                                        <IonCardContent className="ion-padding">
                                            <IonIcon icon={cartOutline} className="empty-icon" />
                                            <h2>Your shopping cart is empty</h2>
                                            <p>Please add products to your cart before proceeding to checkout.</p>
                                            <IonButton color="primary" routerLink={appSubDir} expand="block" className="action-btn">
                                                Shop Now
                                            </IonButton>
                                        </IonCardContent>
                                    </IonCard>
                                </IonCol>
                            </IonRow>
                        ) : !user.token ? (
                            <IonRow className="ion-justify-content-center">
                                <IonCol sizeXs="12" sizeMd="6">
                                    <IonCard className="login-prompt-card ion-text-center">
                                        <IonCardHeader>
                                            <h2>অর্ডার করতে লগইন করুন</h2>
                                        </IonCardHeader>
                                        <IonCardContent className="ion-padding">
                                            <IonIcon icon={logInOutline} className="login-icon" />
                                            <p>আপনার অর্ডারটি সম্পন্ন করতে এবং শিপিং তথ্য দিতে দয়া করে প্রথমে লগইন করুন।</p>
                                            <IonButton onClick={() => authModalRef.current?.present()} color="success" expand="block" className="action-btn">
                                                লগইন করুন / Sign In
                                            </IonButton>
                                        </IonCardContent>
                                    </IonCard>
                                </IonCol>
                            </IonRow>
                        ) : (
                            <IonRow className="checkout-row">
                                {/* Left Side: Checkout Form */}
                                <IonCol sizeXs="12" sizeMd="7" className="checkout-left-col">
                                    {!areConditionsMet ? (
                                        <IonCard color="danger" className="alert-card">
                                            <IonCardContent className="ion-padding">
                                                <div className="alert-content">
                                                    <IonIcon icon={alertCircleOutline} className="alert-icon" />
                                                    <div>
                                                        <h3>অর্ডার শর্তাবলী পূরণ হয়নি</h3>
                                                        <p>
                                                            অর্ডার করতে হলে কমপক্ষে <b>{minOrderAmount} /=</b> টাকার অথবা <b>{minOrderItemCount}</b> টি প্রোডাক্ট সিলেক্ট করতে হবে।
                                                        </p>
                                                        <p className="current-status">
                                                            আপনার বর্তমান অর্ডার: <b>{cart.total} /=</b> (<b>{cart.itemCount}</b> টি প্রোডাক্ট)
                                                        </p>
                                                    </div>
                                                </div>
                                            </IonCardContent>
                                        </IonCard>
                                    ) : (
                                        <IonCard className="checkout-form-card">
                                            <IonCardHeader>
                                                <h2>Shipping Information</h2>
                                            </IonCardHeader>
                                            <IonCardContent>
                                                <CheckoutBody isPage={true} />
                                            </IonCardContent>
                                        </IonCard>
                                    )}
                                </IonCol>

                                {/* Right Side: Cart Summary */}
                                <IonCol sizeXs="12" sizeMd="5" className="checkout-right-col">
                                    <IonCard className="checkout-summary-card">
                                        <IonCardHeader>
                                            <h2>Order Summary</h2>
                                        </IonCardHeader>
                                        <IonCardContent>
                                            <div className="checkout-cart-items">
                                                <IonGrid className="no-padding">
                                                    <IonRow>
                                                        {cart.items.map((item, index) => (
                                                            <IonCol size="12" sizeSm="6" sizeMd="12" key={index} className="no-padding">
                                                                <IonCard className="checkout-item-card">
                                                                    <div className="card-image-container">
                                                                        <ProgressiveImg 
                                                                            src={getProductImageUrl(item.imageUrl || item.img || "", 80)} 
                                                                            alt={item.title} 
                                                                        />
                                                                    </div>
                                                                    <div className="card-details">
                                                                        <h3 className="item-title">{preventOverflow(item.title, 40)}</h3>
                                                                        <p className="item-variant">{item.selectedSku.props_names}</p>
                                                                        <div className="item-price-qty">
                                                                            <span className="qty-tag">Qty: <b>{item.quantity}</b></span>
                                                                            <span className="price-tag">৳{BDT.format(item.unitPrice)}</span>
                                                                        </div>
                                                                        <div className="item-total">
                                                                            <span>Total:</span>
                                                                            <strong className="total-amount">৳{BDT.format(item.itemTotal)}</strong>
                                                                        </div>
                                                                    </div>
                                                                </IonCard>
                                                            </IonCol>
                                                        ))}
                                                    </IonRow>
                                                </IonGrid>
                                            </div>
                                            <div className="checkout-cart-subtotal">
                                                <CartSubTotal />
                                            </div>
                                        </IonCardContent>
                                    </IonCard>
                                </IonCol>
                            </IonRow>
                        )}
                    </IonGrid>
                </IonCol>
            </IonRow>
            <Footer />
        </IonContent>
    );
};

export default Checkout;
